import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { cn } from "@/lib/utils";

/**
 * The launch-film player: the control bar from the Sonar executive film
 * (project-launcher/kit/player), wrapped around an MP4 instead of a canvas.
 *
 * Navy stage, a tilted white "Play" pill over the first frame, and a gold
 * control bar (play/pause, scrubber, time, volume) that fades in on hover and
 * stays up while paused. Space plays/pauses and the arrow keys skip 2 s while the
 * player has focus; the volume is remembered per browser when storage allows.
 */

const VOLUME_KEY = "northstar-launch-volume";

function readVolume(): number {
  try {
    const v = parseFloat(localStorage.getItem(VOLUME_KEY) ?? "");
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
  } catch {
    return 1;
  }
}

function saveVolume(v: number) {
  try {
    localStorage.setItem(VOLUME_KEY, String(v));
  } catch {
    /* storage unavailable: the volume just isn't remembered */
  }
}

const fmt = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

interface LaunchPlayerProps {
  src: string;
  title: string;
  poster?: string | null;
  className?: string;
}

export function LaunchPlayer({ src, title, poster, className }: LaunchPlayerProps) {
  const video = useRef<HTMLVideoElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [ended, setEnded] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(readVolume);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const v = video.current;
    if (!v) return;
    v.volume = volume;
    v.muted = muted || volume === 0;
  }, [volume, muted]);

  const play = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.ended) v.currentTime = 0;
    void v.play();
  }, []);
  const toggle = useCallback(() => {
    const v = video.current;
    if (!v) return;
    if (v.paused || v.ended) play();
    else v.pause();
  }, [play]);
  const seek = useCallback((t: number) => {
    const v = video.current;
    if (!v || !Number.isFinite(v.duration)) return;
    v.currentTime = Math.min(v.duration, Math.max(0, t));
    setTime(v.currentTime);
  }, []);
  const seekFromPointer = (e: PointerEvent<HTMLDivElement>) => {
    const r = bar.current?.getBoundingClientRect();
    if (!r || !duration) return;
    seek(((e.clientX - r.left) / r.width) * duration);
  };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).tagName === "INPUT") return;
    if (e.code === "Space" && (e.target as HTMLElement).tagName !== "BUTTON") {
      e.preventDefault();
      toggle();
    } else if (e.code === "ArrowRight" || e.code === "ArrowLeft") {
      e.preventDefault();
      seek((video.current?.currentTime ?? 0) + (e.code === "ArrowRight" ? 2 : -2));
    }
  };

  const silent = muted || volume === 0;
  const showOverlay = !playing && (!started || ended);
  const pct = duration ? (100 * time) / duration : 0;

  return (
    <div
      tabIndex={0}
      onKeyDown={onKey}
      className={cn(
        "group relative aspect-video w-full overflow-hidden rounded-card bg-[#0b1424] outline-none focus-visible:ring-2 focus-visible:ring-[#AB9151]",
        className,
      )}
    >
      <video
        ref={video}
        src={src}
        poster={poster ?? undefined}
        preload="metadata"
        playsInline
        className="h-full w-full cursor-pointer object-contain"
        aria-label={title}
        onClick={toggle}
        onPlay={() => {
          setPlaying(true);
          setStarted(true);
          setEnded(false);
        }}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setEnded(true);
        }}
        onTimeUpdate={(e) => !dragging.current && setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      <button
        type="button"
        onClick={play}
        aria-label={ended ? "Play again" : `Play: ${title}`}
        className={cn(
          "absolute inset-0 grid place-items-center bg-[rgba(8,12,22,0.35)] transition-opacity duration-300",
          showOverlay ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      >
        <span className="flex -rotate-[1.5deg] items-center gap-3.5 rounded-pill bg-white py-4 pl-5 pr-7 font-sans text-[clamp(16px,2.2vw,26px)] font-bold leading-none text-bw-navy shadow-[0_10px_30px_rgba(0,0,0,.35)]">
          <span className="h-0 w-0 border-y-[13px] border-l-[22px] border-y-transparent border-l-bw-navy" aria-hidden />
          <span className="max-w-[60vw] truncate">{ended ? "Play again" : `Play: ${title}`}</span>
        </span>
      </button>

      <div
        className={cn(
          "absolute inset-x-0 bottom-0 flex items-center gap-3.5 bg-gradient-to-t from-[rgba(8,12,22,0.78)] to-transparent px-4 pb-3 pt-6 transition-opacity duration-200",
          "opacity-0 focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100",
          !playing && "opacity-100",
        )}
      >
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="grid h-[34px] w-[34px] flex-none place-items-center rounded-full bg-[#AB9151] text-bw-navy focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F1E7]"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4 fill-current" aria-hidden>
            <path d={playing ? "M4 2.5h3v11H4zM9 2.5h3v11H9z" : "M4 2.5v11l9-5.5z"} />
          </svg>
        </button>

        <div
          ref={bar}
          role="slider"
          tabIndex={0}
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuenow={Math.round(time)}
          className="relative h-2 min-w-[60px] flex-1 cursor-pointer rounded bg-[rgba(245,241,231,0.22)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#F5F1E7]"
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture(e.pointerId);
            seekFromPointer(e);
          }}
          onPointerMove={(e) => dragging.current && seekFromPointer(e)}
          onPointerUp={() => {
            dragging.current = false;
          }}
        >
          <div className="h-full rounded bg-[#AB9151]" style={{ width: `${pct}%` }} />
        </div>

        <span className="hidden min-w-[78px] flex-none text-right font-sans text-[13px] font-semibold tabular-nums text-[#F5F1E7] sm:inline">
          {fmt(time)} / {fmt(duration)}
        </span>

        <div className="flex flex-none items-center gap-2 text-[#F5F1E7]">
          <button
            type="button"
            aria-label={silent ? "Unmute" : "Mute"}
            className="grid h-[30px] w-[30px] place-items-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#F5F1E7]"
            onClick={() => {
              if (!silent) return setMuted(true);
              if (volume === 0) setVolume(0.8);
              setMuted(false);
            }}
          >
            <svg viewBox="0 0 20 20" className="h-5 w-5 fill-current" aria-hidden>
              <path d="M3 7.5h3l4-3.5v12l-4-3.5H3z" />
              <path
                d="M13 6.5a4.5 4.5 0 0 1 0 7M15.2 4.3a7.6 7.6 0 0 1 0 11.4"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.7}
                strokeLinecap="round"
                style={{ opacity: silent ? 0.15 : 1 }}
              />
            </svg>
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={volume}
            aria-label="Volume"
            className="w-[clamp(64px,12vw,120px)] cursor-pointer accent-[#AB9151]"
            style={{ opacity: muted ? 0.5 : 1 }}
            onChange={(e) => {
              const v = parseFloat(e.target.value);
              setVolume(v);
              setMuted(false);
              saveVolume(v);
            }}
          />
        </div>
      </div>
    </div>
  );
}
